import React, { useEffect, useMemo, useRef, useState } from "react";
import { MoreHorizontal, ChevronRight, ChevronLeft, Search, Check } from "lucide-react";

// PagedKebabMenu — kebab dropdown with in-menu page navigation.
//
// Props:
//   size, testid, className
//   pages: { [pageKey]: {
//       title?, backTo?, searchable?, searchPlaceholder?,
//       items: Array<{ label, icon?, subtitle?, active?, danger?, disabled?, divider?, header?,
//                       toPage?, onClick?, closeOnClick?, testid? }>
//     }
//   }
//   initialPage?: string (default "root")
export function PagedKebabMenu({ size = 14, testid = "kebab", className = "", pages, initialPage = "root" }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(initialPage);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) { setPage(initialPage); setSearch(""); return; }
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open, initialPage]);

  const current = pages[page] || pages[initialPage] || { items: [] };
  const filteredItems = useMemo(() => {
    if (!current.searchable || !search.trim()) return current.items;
    const q = search.toLowerCase();
    return current.items.filter((it) => it.divider || it.header || (it.label || "").toLowerCase().includes(q));
  }, [current, search]);

  return (
    <div ref={ref} className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        data-testid={testid}
        aria-label="Actions"
        className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-900 transition-colors"
      >
        <MoreHorizontal size={size} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 min-w-[220px] max-w-[280px] rounded-md border border-neutral-200 bg-white shadow-lg py-1 z-50"
          data-testid={`${testid}-menu`}
        >
          {page !== initialPage && (
            <button
              onClick={() => { setPage(initialPage); setSearch(""); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-neutral-500 hover:bg-neutral-100 border-b border-neutral-100 mb-1"
              data-testid={`${testid}-back`}
            >
              <ChevronLeft size={12} />
              <span className="font-medium text-neutral-700">{current.title || "Back"}</span>
            </button>
          )}

          {current.searchable && (
            <div className="px-2 py-1.5 border-b border-neutral-100 mb-1">
              <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-neutral-200 bg-neutral-50">
                <Search size={11} className="text-neutral-400" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={current.searchPlaceholder || "Search…"}
                  className="flex-1 bg-transparent outline-none text-xs min-w-0"
                  data-testid={`${testid}-search`}
                />
              </div>
            </div>
          )}

          <div className="max-h-72 overflow-y-auto">
            {filteredItems.map((it, i) => {
              if (it.divider) return <div key={i} className="my-1 border-t border-neutral-100" />;
              if (it.header) return <p key={i} className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-neutral-500 font-medium">{it.header}</p>;
              return (
                <button
                  key={i}
                  onClick={() => {
                    if (it.toPage) { setPage(it.toPage); setSearch(""); return; }
                    if (it.onClick) it.onClick();
                    if (it.closeOnClick !== false) setOpen(false);
                  }}
                  disabled={it.disabled}
                  data-testid={it.testid}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${it.danger ? "text-red-600" : "text-neutral-800"} ${it.active ? "font-medium" : ""}`}
                >
                  {it.icon && <span className="text-neutral-500 shrink-0">{it.icon}</span>}
                  <div className="flex-1 min-w-0 flex items-baseline gap-2">
                    <span className="truncate">{it.label}</span>
                    {it.subtitle && <span className="text-[10px] text-neutral-500 truncate">{it.subtitle}</span>}
                  </div>
                  {it.active && <Check size={12} className="text-neutral-500 shrink-0" />}
                  {it.toPage && <ChevronRight size={12} className="text-neutral-400 shrink-0" />}
                </button>
              );
            })}
            {filteredItems.length === 0 && (
              <p className="px-3 py-3 text-[11px] italic text-neutral-400 text-center">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
